/**
 * Formatting for the dashboard. Every amount arrives in cents of the summary's
 * currency (USD: every operator runs at a 1.0 rate), so money is printed as
 * currency; counts and shares as they are. Anything that is not a finite
 * number reads as a dash, never as NaN.
 */

function figure(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formatCents(cents: number | null | undefined, currency: string): string {
  const amount = figure(cents);
  if (amount === null) return '—';

  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
}

export function formatCount(count: number | null | undefined): string {
  const value = figure(count);

  return value === null ? '—' : value.toLocaleString('en-US');
}

/** A 0..1 share as a percentage. */
export function formatShare(share: number | null | undefined): string {
  const value = figure(share);

  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

// Parsed at UTC noon so no timezone can push a day into its neighbour.
function utcNoon(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

const DAY_LABEL = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const SHORT_DAY_LABEL = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** "2026-09-22" → "Sep 22, 2026". */
export function formatDay(day: string): string {
  const parsed = utcNoon(day);
  return Number.isNaN(parsed.getTime()) ? day : DAY_LABEL.format(parsed);
}

/** "2026-09-22" → "Sep 22", for chart ticks. */
export function formatShortDay(day: string): string {
  const parsed = utcNoon(day);
  return Number.isNaN(parsed.getTime()) ? day : SHORT_DAY_LABEL.format(parsed);
}

/** 9 → "09:00 UTC". */
export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00 UTC`;
}
